import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { MatrixQuote } from '@atoms';
import { MatrixRain } from '@molecules';

import WithCenter from '../../layout/WithCenter';

import styles from './TopLevelErrorBoundary.module.scss';

function PillButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className={styles.button}
      onClick={() => {
        onClick();
        navigate('login');
      }}
    >
      {children}
    </button>
  );
}

type Props = {
  children: React.ReactNode;
} & WithTranslation;

type State = {
  hasError: boolean;
};

class TopLevelErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    Sentry.captureException({
      ...error,
      ...errorInfo,
    });
  }

  redirect = () => {
    this.setState({ hasError: false });
  };

  render() {
    const { hasError } = this.state;
    const { t, children } = this.props;

    if (hasError) {
      return (
        <WithCenter>
          <div className={styles.container}>
            <div className="container-two">
              <h1 className={styles.h1}>{t('errors.error_unhandled')}</h1>
              <MatrixQuote />
              <PillButton onClick={this.redirect}>{t('back_to_login')}</PillButton>
            </div>
          </div>
          <MatrixRain />
        </WithCenter>
      );
    }

    return children;
  }
}

export default withTranslation()(TopLevelErrorBoundary);
